// Firebase Firestore imports for database operations
import { collection, getDocs } from "firebase/firestore";
// Firebase configuration and database instance
import { Firestore } from "../components/FirebaseAuth/firebase";

/**
 * Site Count Service
 * 
 * This service provides centralized functionality for counting sites within organizations.
 * It follows service-oriented architecture principles to eliminate code duplication between
 * the Home dashboard and Organization List pages.
 * 
 * The service handles the business logic of retrieving site counts from Firestore collections,
 * with fallback logic for different data sources and error handling.
 */
export const siteCountService = {
  
  /**
   * Get total site count for a single organization
   * 
   * This function retrieves the total number of sites for an organization by querying
   * Firestore subcollections. It implements a fallback strategy:
   * 1. First tries to read from 'cmsSummary' collection (newer data structure)
   * 2. Falls back to 'siteTypeSummary' collection (legacy data structure)
   * 3. Aggregates counts from all documents in the chosen collection
   * 
   * @param {string} orgId - The organization ID to get site count for
   * @returns {Promise<number>} - Total number of sites for the organization
   */
  async getSiteCountForOrganization(orgId) {
    try {
      // Validate input parameter - return 0 for invalid/missing org ID
      if (!orgId) {
        return 0;
      }

      // Primary data source: Try to fetch from cmsSummary subcollection first
      // This is the preferred data source as it contains more current CMS information
      let summaryRef = collection(Firestore, "organizations", orgId, "cmsSummary");
      let summaryData = await getDocs(summaryRef);

      // Fallback strategy: If cmsSummary is empty, try siteTypeSummary
      // This ensures backward compatibility with older data structures
      if (summaryData.empty) {
        summaryRef = collection(Firestore, "organizations", orgId, "siteTypeSummary");
        summaryData = await getDocs(summaryRef);
      }

      // Aggregate total count across all summary documents
      // Each document in the collection represents a different site type/CMS
      // with a 'count' field indicating how many sites use that type
      // This logic matches exactly what's used in Home.js line 456
      return summaryData.docs.reduce((sum, doc) => {
        const docData = doc.data();
        const count = docData.count || 0; // Ensure we have a valid number
        return sum + count;
      }, 0);
      
    } catch (error) {
      // Log error for debugging but don't throw - return safe default
      console.error(`Error fetching site count for org ${orgId}:`, error);
      return 0;
    }
  },

  /**
   * Get site counts for multiple organizations in a single batch operation
   * 
   * This function optimizes performance by fetching site counts for multiple organizations
   * concurrently using Promise.all. This is much more efficient than sequential requests
   * when displaying organization lists.
   * 
   * @param {string[]} orgIds - Array of organization IDs to get site counts for
   * @returns {Promise<Object>} - Object mapping orgId to site count {orgId: count}
   */
  async batchGetSiteCounts(orgIds) {
    // Validate input - return empty object for invalid input
    if (!orgIds || orgIds.length === 0) {
      return {};
    }
    
    try {
      // Execute all site count requests concurrently for better performance
      // This is significantly faster than sequential await calls in a loop
      const counts = await Promise.all(
        orgIds.map(id => this.getSiteCountForOrganization(id))
      );
      
      // Transform parallel results array into a lookup object
      // Creates mapping: {orgId1: count1, orgId2: count2, ...}
      // This structure allows O(1) lookup time when rendering organization items
      return Object.fromEntries(orgIds.map((id, i) => [id, counts[i]]));
      
    } catch (error) {
      // Log error for debugging but return safe default object
      console.error("Error batch fetching site counts:", error);
      return {};
    }
  }
};